package com.schoolconnect.app.feature.mystudents

import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.response.StudentListResponse
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.feature.students.StudentGuardianUi
import com.schoolconnect.app.feature.students.StudentRowUi
import com.schoolconnect.app.repository.mystudents.TeacherMyStudentsRepository
import com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private fun Double.toDisplayExamNumber(): String = if (this % 1.0 == 0.0) toInt().toString() else toString()

class TeacherMyStudentsViewModel {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] TeacherMyStudentsViewModel"
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private val _uiState = MutableStateFlow(MyStudentsUiState(isLoading = true))
    val uiState: StateFlow<MyStudentsUiState> = _uiState.asStateFlow()

    init {
        loadStatistics()
        loadPage()
    }

    fun onEvent(event: MyStudentsUiEvent) {
        when (event) {
            MyStudentsUiEvent.RefreshRequested -> {
                loadStatistics()
                loadPage()
            }
            is MyStudentsUiEvent.SearchChanged -> {
                _uiState.update { it.copy(searchQuery = event.query, pageIndex = 0) }
                loadPage(debounceMs = 250)
            }
            is MyStudentsUiEvent.FiltersChanged -> {
                _uiState.update { it.copy(filters = event.filters, pageIndex = 0) }
                loadPage()
            }
            MyStudentsUiEvent.FiltersReset -> {
                _uiState.update { it.copy(filters = MyStudentsFilterValues(), searchQuery = "", pageIndex = 0) }
                loadPage()
            }
            is MyStudentsUiEvent.PageChanged -> {
                _uiState.update { it.copy(pageIndex = event.pageIndex.coerceAtLeast(0)) }
                loadPage()
            }
            is MyStudentsUiEvent.PageSizeChanged -> {
                _uiState.update {
                    it.copy(
                        pageIndex = 0,
                        pageSize = event.pageSize.coerceIn(
                            PaginationPolicy.PAGE_SIZE_OPTIONS.first(),
                            PaginationPolicy.PAGE_SIZE_OPTIONS.last()
                        )
                    )
                }
                loadPage()
            }
            MyStudentsUiEvent.ClearError -> _uiState.update { it.copy(errorMessage = "") }
            is MyStudentsUiEvent.ViewStudentRequested -> loadStudentDetail(event.studentId)
            MyStudentsUiEvent.StudentDetailDismissed -> _uiState.update {
                it.copy(studentDetail = null, isStudentDetailLoading = false, studentDetailError = "")
            }
            is MyStudentsUiEvent.ViewExamResultsRequested -> loadStudentExamResults(event.studentId)
            MyStudentsUiEvent.StudentExamResultsDismissed -> _uiState.update {
                it.copy(studentExamResults = emptyList(), isStudentExamResultsLoading = false, studentExamResultsError = "")
            }
            is MyStudentsUiEvent.SortChanged -> {
                val ascending = if (_uiState.value.sortField == event.field) !_uiState.value.sortAscending else true
                _uiState.update { it.copy(sortField = event.field, sortAscending = ascending, pageIndex = 0) }
                loadPage()
            }
        }
    }

    private fun loadStudentDetail(studentId: String) {
        val normalizedStudentId = studentId.trim()
        if (normalizedStudentId.isBlank()) {
            _uiState.update { it.copy(studentDetail = null, isStudentDetailLoading = false, studentDetailError = "Student details are unavailable for this row.") }
            return
        }
        // Clear stale detail and publish the pending state before the coroutine
        // starts so a rapid second selection cannot briefly show the previous row.
        _uiState.update { it.copy(studentDetail = null, isStudentDetailLoading = true, studentDetailError = "") }
        scope.launch {
            TeacherMyStudentsRepository.loadStudentDetail(normalizedStudentId).fold(
                onSuccess = { response ->
                    _uiState.update { it.copy(studentDetail = response.toDetailUi(), isStudentDetailLoading = false, studentDetailError = "") }
                },
                onFailure = { throwable ->
                    scLog("$TAG loadStudentDetail failure studentId=$normalizedStudentId", throwable)
                    _uiState.update {
                        it.copy(
                            studentDetail = null,
                            isStudentDetailLoading = false,
                            studentDetailError = throwable.message.orEmpty().ifBlank { "Unable to load student details." }
                        )
                    }
                }
            )
        }
    }

    private fun loadStudentExamResults(studentId: String) {
        val normalizedStudentId = studentId.trim()
        if (normalizedStudentId.isBlank()) {
            _uiState.update { it.copy(studentExamResults = emptyList(), isStudentExamResultsLoading = false, studentExamResultsError = "Exam results are unavailable for this row.") }
            return
        }
        scope.launch {
            _uiState.update { it.copy(studentExamResults = emptyList(), isStudentExamResultsLoading = true, studentExamResultsError = "") }
            TeacherMyStudentsRepository.loadStudentExamResults(normalizedStudentId).fold(
                onSuccess = { response ->
                    _uiState.update { it.copy(studentExamResults = response.items.orEmpty().map { result ->
                        TeacherStudentExamResultUi(
                            examId = result.examId.orEmpty().ifBlank { "—" },
                            subjectId = result.subjectId.orEmpty().ifBlank { "—" },
                            marks = result.marksObtained?.toDisplayExamNumber() ?: "—",
                            internalScore = result.internalScore?.toDisplayExamNumber() ?: "—",
                            mainScore = result.mainScore?.toDisplayExamNumber() ?: "—",
                            grade = result.grade.orEmpty().ifBlank { "—" },
                            rank = result.rank?.toString() ?: "—",
                            status = if (result.isAbsent == true) "Absent" else "Present",
                            remarks = result.teacherRemarks ?: result.notes ?: "—"
                        )
                    }, isStudentExamResultsLoading = false, studentExamResultsError = "") }
                },
                onFailure = { throwable ->
                    _uiState.update { it.copy(isStudentExamResultsLoading = false, studentExamResultsError = throwable.message.orEmpty().ifBlank { "Unable to load exam results." }) }
                }
            )
        }
    }

    private fun loadPage(debounceMs: Long = 0) {
        loadJob?.cancel()
        loadJob = scope.launch {
            if (debounceMs > 0) delay(debounceMs)
            val currentState = _uiState.value
            val selectedGender = currentState.filters.gender.trim().takeIf { it.isNotBlank() }
            val genderCode = selectedGender?.let { SystemLookupOptions.code("GENDER", it) }
            if (selectedGender != null && genderCode.isNullOrBlank()) {
                _uiState.update {
                    it.copy(
                        rows = emptyList(),
                        totalCount = 0,
                        isLoading = false,
                        errorMessage = "The selected gender value is unavailable. Please refresh and try again."
                    )
                }
                return@launch
            }
            _uiState.update { it.copy(isLoading = true, errorMessage = "") }
            val classSectionId = currentState.filters.classSectionId.trim().takeIf { it.isNotBlank() }

            // Send every search term, including numeric roll-number queries, to
            // the paged API. Do not load the complete roster for local search.
            val pageResult = TeacherMyStudentsRepository.loadPage(
                query = currentState.searchQuery,
                gender = genderCode,
                classSectionId = classSectionId,
                pageIndex = currentState.pageIndex,
                pageSize = currentState.pageSize,
                sort = currentState.sortField.toServerSort(currentState.sortAscending)
            )
            pageResult.fold(
                onSuccess = { response ->
                    coroutineContext.ensureActive()
                    val rows = response.students.map(::toRow)
                    val identifiedRows = rows.count { !it.studentId.isNullOrBlank() }
                    scLog("$TAG loadPage student identifiers=$identifiedRows/${rows.size}")
                    _uiState.update { current ->
                        val loadedState = current.copy(
                            rows = rows,
                            totalCount = response.count ?: rows.size,
                            pageIndex = current.pageIndex,
                            activeAcademicYearId = response.academicYearId.orEmpty(),
                            isLoading = false,
                            errorMessage = ""
                        )
                        loadedState
                    }
                },
                onFailure = { throwable ->
                    if (throwable is CancellationException) throw throwable
                    coroutineContext.ensureActive()
                    scLog("$TAG loadPage failure", throwable)
                    _uiState.update { it.copy(rows = emptyList(), totalCount = 0, isLoading = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load students." }) }
                }
            )
        }
    }

    private fun loadStatistics() {
        scope.launch {
            TeacherMyStudentsRepository.loadDashboardStatistics().fold(
                onSuccess = { response ->
                    _uiState.update {
                        it.copy(
                            dashboardTotalStudents = response.totalStudents,
                            dashboardAssignedClasses = response.assignedClassesCount,
                            dashboardGoodPerformance = response.goodPerformanceCount,
                            dashboardNeedsAttention = response.needsAttentionCount,
                            classOptions = response.assignedClasses.mapNotNull { classStatistics ->
                                val id = classStatistics.classSectionId?.trim().orEmpty()
                                val label = listOfNotNull(classStatistics.className, classStatistics.sectionName).joinToString("-")
                                if (id.isBlank() || label.isBlank()) null else MyStudentsClassOption(id, label)
                            }.distinctBy { option -> option.id }.sortedWith(compareBySmartText { option -> option.label })
                        )
                    }
                },
                onFailure = { throwable -> scLog("$TAG loadStatistics failure", throwable) }
            )
        }
    }

    private fun toRow(student: com.schoolconnect.app.datasource.network.response.TeacherStudentListItem): MyStudentRowUi {
        val classLabel = listOfNotNull(student.className, student.sectionName).joinToString("-").ifBlank { "—" }
        return MyStudentRowUi(
            key = student.studentId?.takeIf { it.isNotBlank() } ?: student.id.orEmpty(),
            studentId = student.studentId?.trim()?.takeIf { it.isNotBlank() } ?: student.id?.trim()?.takeIf { it.isNotBlank() },
            rollNumber = student.rollNumber.orEmpty().ifBlank { "—" },
            studentName = student.name.orEmpty().ifBlank { "—" },
            classLabel = classLabel,
            gender = student.gender.orEmpty().ifBlank { "—" },
            attendancePercentage = student.attendancePercentage,
            examGrade = student.averageExamGrade?.toString().orEmpty(),
            examSubject = "",
            subjectCodes = student.subjectCodes.orEmpty().mapNotNull { it.trim().takeIf(String::isNotBlank) }.distinct()
        )
    }

    private fun StudentListResponse.toDetailUi(): StudentRowUi {
        val activeParents = parents.orEmpty().filter { it.isActive != false }
        fun StudentListResponse.parent(primary: Boolean): StudentGuardianUi = activeParents
            .firstOrNull { if (primary) it.isPrimaryContact == true else it.isPrimaryContact != true }
            ?.let {
                StudentGuardianUi(
                    name = it.name.orEmpty().ifBlank { "—" },
                    relationCode = it.relationshipType.toRelationCode(),
                    mobile = it.mobile.orEmpty().ifBlank { "—" },
                    email = it.email.orEmpty().ifBlank { "—" }
                )
            }
            ?: StudentGuardianUi("—", if (primary) SchoolKeys.V_REL_FATHER else SchoolKeys.V_REL_MOTHER, "—", "—")

        return StudentRowUi(
            id = id.orEmpty(),
            admissionNo = admissionNumber.orEmpty().ifBlank { "—" },
            studentName = name.orEmpty().ifBlank { "—" },
            className = className.orEmpty().ifBlank { "—" },
            section = sectionName.orEmpty().ifBlank { "—" },
            rollNo = rollNumber.orEmpty().ifBlank { "—" },
            genderCode = gender.orEmpty(),
            bloodGroupCode = bloodGroup.orEmpty(),
            subjectCodes = subjects.orEmpty().filter { it.isActive != false }.mapNotNull { it.subjectCode?.trim()?.takeIf(String::isNotBlank) }.distinct(),
            statusCode = if (isActive == true) SchoolKeys.V_STATUS_ACTIVE else SchoolKeys.V_STATUS_INACTIVE,
            createdDate = createdAt.toDisplayDate(),
            updatedDate = updatedAt.toDisplayDate(),
            createdAtText = createdAt.toDisplayDate(),
            updatedAtText = updatedAt.toDisplayDate(),
            createdBy = createdByName.orEmpty().ifBlank { createdById.orEmpty().ifBlank { "—" } },
            updatedBy = updatedByName.orEmpty().ifBlank { updatedById.orEmpty().ifBlank { "—" } },
            primaryGuardian = parent(primary = true),
            secondaryGuardian = parent(primary = false),
            dateOfBirth = dob.toDisplayDate()
        )
    }

    private fun String?.toDisplayDate(): String {
        val value = this.orEmpty().trim().take(10)
        val parts = value.split("-")
        if (parts.size != 3) return value.ifBlank { "—" }
        val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
            .getOrNull(parts[1].toIntOrNull()?.minus(1) ?: -1) ?: return value
        return "${parts[2].padStart(2, '0')} $month ${parts[0]}"
    }

    private fun String?.toRelationCode(): String = when (this.orEmpty().trim().lowercase()) {
        "father" -> SchoolKeys.V_REL_FATHER
        "mother" -> SchoolKeys.V_REL_MOTHER
        "guardian" -> SchoolKeys.V_REL_GUARDIAN
        else -> this.orEmpty()
    }

    fun clear() {
        loadJob?.cancel()
        scope.cancel()
    }
}
